/**
 * GUAPI Quick Shop — vanilla JS, no dependencies.
 *
 * Responsibilities:
 *  - per-card swipeable gallery (dots + arrows sync to scroll position)
 *  - size-aware Quick Add: resolves the EXACT variant id for the picked size
 *    from the per-card JSON variant map, never falls back to a blind
 *    "first available variant" add on a sized product
 *  - AJAX add-to-cart wired to this theme's native <cart-drawer> component
 *    (falls back gracefully if the theme doesn't have one)
 *  - sticky CART SUMMARY bar: item thumbnails (when there's room), item
 *    count + subtotal, and a direct-to-checkout button (skips /cart) —
 *    always reflects the REAL cart state (fetched from /cart.js), not just
 *    "the last thing you clicked"
 *  - "Load more" pagination via Shopify's section rendering API, with
 *    scoped re-init of the newly appended cards only (no double-binding)
 */
(function () {
  "use strict";

  function initGallery(gallery) {
    if (gallery.dataset.guapiQsInit === "true") return;
    gallery.dataset.guapiQsInit = "true";

    var track = gallery.querySelector("[data-guapi-qs-track]");
    var prevBtn = gallery.querySelector("[data-guapi-qs-prev]");
    var nextBtn = gallery.querySelector("[data-guapi-qs-next]");
    var dots = gallery.querySelectorAll("[data-guapi-qs-dot]");
    if (!track) return;

    var imgCount = track.querySelectorAll("img").length;

    function activeIndex() {
      var w = track.clientWidth || 1;
      return Math.round(track.scrollLeft / w);
    }

    function scrollToIndex(i) {
      var w = track.clientWidth;
      track.scrollTo({ left: w * i, behavior: "smooth" });
    }

    function syncUI() {
      var idx = activeIndex();
      dots.forEach(function (dot, i) {
        dot.setAttribute("data-active", i === idx ? "true" : "false");
      });
      if (prevBtn) prevBtn.style.visibility = idx === 0 ? "hidden" : "visible";
      if (nextBtn) {
        nextBtn.style.visibility = idx === imgCount - 1 ? "hidden" : "visible";
      }
    }

    track.addEventListener("scroll", function () {
      window.requestAnimationFrame(syncUI);
    });

    if (prevBtn) {
      prevBtn.addEventListener("click", function (e) {
        e.preventDefault();
        e.stopPropagation();
        scrollToIndex(Math.max(0, activeIndex() - 1));
      });
    }
    if (nextBtn) {
      nextBtn.addEventListener("click", function (e) {
        e.preventDefault();
        e.stopPropagation();
        scrollToIndex(Math.min(imgCount - 1, activeIndex() + 1));
      });
    }

    syncUI();
  }

  function getVariantMap(card) {
    var script = card.querySelector("[data-guapi-qs-variant-map]");
    if (!script) return null;
    try {
      return JSON.parse(script.textContent);
    } catch (e) {
      console.error("guapi-qs: failed to parse variant map", e);
      return null;
    }
  }

  function initSizePicker(card) {
    if (card.dataset.guapiQsSizeInit === "true") return;
    card.dataset.guapiQsSizeInit = "true";

    var pills = card.querySelectorAll("[data-guapi-qs-size-pill]");
    var quickAdd = card.querySelector("[data-guapi-qs-quickadd]");
    if (!pills.length || !quickAdd) return;

    var variantMap = getVariantMap(card);
    var selectedSize = null;

    pills.forEach(function (pill) {
      pill.addEventListener("click", function (e) {
        e.preventDefault();
        e.stopPropagation();
        if (pill.getAttribute("data-disabled") === "true") return;

        selectedSize = pill.getAttribute("data-size-value");
        pills.forEach(function (p) {
          p.setAttribute("data-selected", p === pill ? "true" : "false");
        });

        var variant = variantMap ? variantMap[selectedSize] : null;
        if (variant && variant.available && variant.id) {
          quickAdd.disabled = false;
          quickAdd.dataset.state = "ready";
          quickAdd.dataset.variantId = variant.id;
          quickAdd.textContent = "Add · " + selectedSize;
        } else {
          quickAdd.disabled = true;
          quickAdd.dataset.state = "soldout";
          delete quickAdd.dataset.variantId;
          quickAdd.textContent = "Sold out";
        }
      });
    });
  }

  // ---- Cart integration -------------------------------------------------

  function formatMoney(cents, moneyFormat) {
    var amount = (cents / 100).toFixed(2);
    // shop.money_format can contain HTML (e.g. `<span class="money">${{amount}}</span>`)
    // for styling elsewhere on the site. We render into textContent here, so any
    // HTML tags would show up as literal text (the "<span..." bug). Strip tags
    // first — we only want the plain numeric/currency text, never markup.
    var plainFormat = moneyFormat ? moneyFormat.replace(/<[^>]*>/g, "") : "";
    if (plainFormat.indexOf("{{amount}}") > -1) {
      return plainFormat.replace("{{amount}}", amount);
    }
    if (plainFormat.indexOf("{{ amount }}") > -1) {
      return plainFormat.replace("{{ amount }}", amount);
    }
    return "$" + amount;
  }

  function refreshHeaderCartCount(cart) {
    document
      .querySelectorAll("[data-cart-count], .cart-count, #cart-icon-bubble")
      .forEach(function (el) {
        if ("textContent" in el) el.textContent = cart.item_count;
      });
  }

  /**
   * Pulls the REAL cart state from /cart.js and renders the cart summary bar:
   * up to 4 item thumbnails (hidden entirely below a certain screen width so
   * it never crowds out the count/subtotal/checkout button), item count,
   * subtotal, and shows/hides the whole bar based on item_count.
   */
  function refreshCartBar() {
    var bar = document.querySelector("[data-guapi-qs-cart-bar]");
    if (!bar) return;

    fetch("/cart.js")
      .then(function (res) {
        return res.json();
      })
      .then(function (cart) {
        refreshHeaderCartCount(cart);

        if (!cart.item_count) {
          bar.setAttribute("data-visible", "false");
          return;
        }

        var moneyFormat = bar.getAttribute("data-money-format");
        var thumbsEl = bar.querySelector("[data-guapi-qs-cart-thumbs]");
        var countEl = bar.querySelector("[data-guapi-qs-cart-count]");
        var totalEl = bar.querySelector("[data-guapi-qs-cart-total]");

        if (thumbsEl) {
          thumbsEl.innerHTML = "";
          cart.items.slice(0, 4).forEach(function (item) {
            var img = document.createElement("img");
            img.className = "guapi-qs-cart-bar-thumb";
            img.src = item.image
              ? item.image.replace(/(\.[a-z]+)(\?|$)/i, "_x80$1$2")
              : "";
            img.alt = "";
            thumbsEl.appendChild(img);
          });
        }

        if (countEl) {
          countEl.textContent =
            cart.item_count + (cart.item_count === 1 ? " item" : " items");
        }
        if (totalEl) {
          totalEl.textContent = formatMoney(cart.total_price, moneyFormat);
        }

        bar.setAttribute("data-visible", "true");
      })
      .catch(function () {});
  }

  function showToast(text) {
    var toast = document.querySelector("[data-guapi-qs-toast]");
    if (!toast) return;
    toast.textContent = text;
    toast.setAttribute("data-visible", "true");
    window.setTimeout(function () {
      toast.setAttribute("data-visible", "false");
    }, 1800);
  }

  function initQuickAdd(card) {
    var quickAdd = card.querySelector("[data-guapi-qs-quickadd]");
    if (!quickAdd || quickAdd.dataset.guapiQsClickInit === "true") return;
    quickAdd.dataset.guapiQsClickInit = "true";

    quickAdd.addEventListener("click", function (e) {
      e.preventDefault();
      e.stopPropagation();

      if (quickAdd.disabled || quickAdd.dataset.state === "soldout") return;

      var variantId = quickAdd.dataset.variantId;
      if (!variantId) return; // no size picked yet on a sized product

      quickAdd.dataset.state = "adding";
      var originalLabel = quickAdd.textContent;
      quickAdd.textContent = "Adding…";

      fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] }),
      })
        .then(function (res) {
          if (!res.ok) throw new Error("Add to cart failed");
          return res.json();
        })
        .then(function () {
          quickAdd.dataset.state = "added";
          quickAdd.textContent = "Added";
          showToast(originalLabel === "Add to cart" ? "Added to cart" : originalLabel.replace("Add ·", "") + " added");
          // Refresh the persistent cart summary bar (thumbnails, count,
          // subtotal) rather than popping open the cart drawer — the whole
          // point of quick-add on the grid is to keep shopping without
          // interruption; the bar is the running total, always visible.
          refreshCartBar();

          window.setTimeout(function () {
            quickAdd.dataset.state = "ready";
            quickAdd.textContent = originalLabel;
          }, 1400);
        })
        .catch(function (err) {
          console.error("guapi-qs: add to cart failed", err);
          quickAdd.dataset.state = "ready";
          quickAdd.textContent = originalLabel;
          showToast("Could not add to cart — try again");
        });
    });
  }

  function initCartBarCheckout() {
    // querySelectorAll, not querySelector — a page can have more than one
    // Quick Shop section (e.g. homepage + collection page stacked, or two
    // grids on the same page), each rendering its own cart bar/button. Using
    // querySelector here bound only the FIRST button found; every other
    // instance's Checkout button looked clickable but silently did nothing.
    var ctas = document.querySelectorAll("[data-guapi-qs-cart-checkout]");
    ctas.forEach(function (cta) {
      if (cta.dataset.guapiQsInit === "true") return;
      cta.dataset.guapiQsInit = "true";
      cta.addEventListener("click", function () {
        // Goes straight to Shopify's checkout, skipping the cart page —
        // as requested. Shopify's own /checkout route handles an empty-cart
        // edge case gracefully (redirects back), so no extra guard needed.
        window.location.href = "/checkout";
      });
    });
  }


  function initCard(card) {
    var gallery = card.querySelector("[data-guapi-qs-gallery]");
    if (gallery) initGallery(gallery);
    initSizePicker(card);
    initQuickAdd(card);
  }

  function initAllCards(root) {
    (root || document).querySelectorAll("[data-guapi-qs-card]").forEach(initCard);
  }

  // ---- Load more ----------------------------------------------------------

  function initLoadMore() {
    var btn = document.querySelector("[data-guapi-qs-loadmore]");
    if (!btn || btn.dataset.guapiQsInit === "true") return;
    btn.dataset.guapiQsInit = "true";

    btn.addEventListener("click", function () {
      var nextUrl = btn.getAttribute("data-next-url");
      var sectionId = btn.getAttribute("data-section-id");
      if (!nextUrl) return;

      btn.setAttribute("data-loading", "true");
      var originalText = btn.textContent;
      btn.textContent = "Loading…";

      var sep = nextUrl.indexOf("?") > -1 ? "&" : "?";
      fetch(nextUrl + sep + "section_id=" + sectionId)
        .then(function (res) {
          return res.text();
        })
        .then(function (html) {
          var parser = new DOMParser();
          var doc = parser.parseFromString(html, "text/html");

          var newGrid = doc.querySelector("[data-guapi-qs-grid]");
          var curGrid = document.querySelector("[data-guapi-qs-grid]");
          if (newGrid && curGrid) {
            var newCards = Array.prototype.slice.call(
              newGrid.querySelectorAll("[data-guapi-qs-card]"),
            );
            newCards.forEach(function (card) {
              curGrid.appendChild(card);
            });
            initAllCards(curGrid);
          }

          var newLoadMoreWrap = doc.querySelector(".guapi-qs-loadmore-wrap");
          var curLoadMoreWrap = document.querySelector(".guapi-qs-loadmore-wrap");
          if (curLoadMoreWrap) {
            if (newLoadMoreWrap) {
              curLoadMoreWrap.outerHTML = newLoadMoreWrap.outerHTML;
              initLoadMore();
            } else {
              curLoadMoreWrap.remove();
            }
          }
        })
        .catch(function (err) {
          console.error("guapi-qs: load more failed", err);
          btn.removeAttribute("data-loading");
          btn.textContent = originalText;
        });
    });
  }

  function init() {
    initAllCards(document);
    initLoadMore();
    initCartBarCheckout();
    // Show the cart bar immediately if the shopper already has items in
    // their cart from an earlier visit/page — don't wait for a new add.
    refreshCartBar();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
